/**
 * Workspace Scope System
 * 
 * Manages two modes:
 * - "single": Filter by one specific workspace
 * - "all": Aggregate data from all accessible workspaces
 */

export type WorkspaceScope = 
  | { type: 'single'; workspaceId: string }
  | { type: 'all'; workspaceIds: string[] };

/**
 * Get current workspace scope from context
 */
export function getWorkspaceScope(
  activeWorkspaceId: string | null,
  accessibleWorkspaceIds: string[]
): WorkspaceScope {
  if (activeWorkspaceId) {
    return { type: 'single', workspaceId: activeWorkspaceId };
  }
  
  // If no active workspace, use "all" with accessible workspaces
  return { type: 'all', workspaceIds: accessibleWorkspaceIds };
}

/**
 * Apply workspace scope filter to a Supabase query
 * 
 * CRITICAL: This is the SINGLE SOURCE OF TRUTH for workspace filtering.
 * All queries must use this helper to ensure consistency.
 * 
 * @param query - Supabase query builder
 * @param scope - WorkspaceScope object (single or all)
 * @returns Query with workspace_id filter applied
 */
export function applyWorkspaceScope<T>(
  query: any,
  scope: WorkspaceScope
): any {
  // DEV LOGGING: Track what filter is being applied
  if (process.env.NODE_ENV === 'development') {
    if (scope.type === 'single') {
      console.log('[applyWorkspaceScope] Applying single workspace filter:', scope.workspaceId);
    } else {
      console.log('[applyWorkspaceScope] Applying all workspaces filter:', {
        workspaceIds: scope.workspaceIds,
        count: scope.workspaceIds.length,
      });
    }
  }
  
  if (scope.type === 'single') {
    if (!scope.workspaceId) {
      // Invalid single scope - return empty result safely
      if (process.env.NODE_ENV === 'development') {
        console.warn('[applyWorkspaceScope] Invalid single scope: workspaceId is null/undefined');
      }
      return query.eq('workspace_id', '00000000-0000-0000-0000-000000000000').limit(0);
    }
    const filteredQuery = query.eq('workspace_id', scope.workspaceId);
    if (process.env.NODE_ENV === 'development') {
      console.log('[applyWorkspaceScope] Single filter applied: workspace_id =', scope.workspaceId);
    }
    return filteredQuery;
  }
  
  // For "all", filter by IN clause
  if (scope.workspaceIds.length === 0) {
    // CRITICAL: This should never happen if WorkspaceScopeContext is working correctly
    // Log warning and return empty result safely
    if (process.env.NODE_ENV === 'development') {
      console.error('[applyWorkspaceScope] CRITICAL: scope.workspaceIds is empty in all mode. This indicates a bug in WorkspaceScopeContext.', { scope });
    }
    return query.eq('workspace_id', '00000000-0000-0000-0000-000000000000').limit(0);
  }
  
  if (scope.workspaceIds.length === 1) {
    // Single workspace in "all" mode - use eq for better performance
    const filteredQuery = query.eq('workspace_id', scope.workspaceIds[0]);
    if (process.env.NODE_ENV === 'development') {
      console.log('[applyWorkspaceScope] All mode with single workspace, using eq:', scope.workspaceIds[0]);
    }
    return filteredQuery;
  }
  
  // Multiple workspaces - use IN clause
  const filteredQuery = query.in('workspace_id', scope.workspaceIds);
  if (process.env.NODE_ENV === 'development') {
    console.log('[applyWorkspaceScope] All mode with multiple workspaces, using IN:', scope.workspaceIds);
  }
  return filteredQuery;
}

/**
 * Apply workspace filter using effective workspace IDs directly
 * This is a more direct helper that takes an array of workspace IDs
 * 
 * @param query - Supabase query builder
 * @param workspaceIds - Array of workspace IDs to filter by
 * @returns Query with workspace_id filter applied
 */
export function applyWorkspaceFilter<T>(
  query: any,
  workspaceIds: string[]
): any {
  if (workspaceIds.length === 0) {
    // No workspaces - return empty result safely
    return query.eq('workspace_id', '00000000-0000-0000-0000-000000000000').limit(0);
  }
  
  if (workspaceIds.length === 1) {
    // Single workspace - use eq for better performance
    return query.eq('workspace_id', workspaceIds[0]);
  }
  
  // Multiple workspaces - use IN clause
  return query.in('workspace_id', workspaceIds);
}

/**
 * Apply workspace scope filter with optional business_unit_id
 * (for backward compatibility, but business_unit_id should match workspace_id in new model)
 */
export function applyWorkspaceScopeWithBU<T>(
  query: any,
  scope: WorkspaceScope,
  businessUnitId?: string | null
): any {
  let filteredQuery = applyWorkspaceScope(query, scope);
  
  // If business_unit_id is specified, add it as additional filter
  // Note: In new model, business_unit_id should match workspace_id, but we keep this for safety
  if (businessUnitId) {
    filteredQuery = filteredQuery.eq('business_unit_id', businessUnitId);
  }
  
  return filteredQuery;
}

